import {
  DndContext,
  DragEndEvent,
  DragMoveEvent,
  DragOverEvent,
  DragOverlay,
  DragStartEvent,
  DropAnimation,
  Modifier,
  UniqueIdentifier,
  closestCenter,
  defaultDropAnimation,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useSelector } from '@xstate/store/react';
import capitalize from 'lodash/capitalize';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLatest, useUnmount } from 'react-use';

import { usePreventAutoReloadOnNewVersion } from '@eluve/blocks';
import {
  Button,
  DebugJson,
  FormItem,
  Heading,
  Icon,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Separator,
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
  Stack,
  Text,
  Textarea,
  useToast,
} from '@eluve/components';
import { LlmOutputTypesEnum, LlmOutputTypesLookup } from '@eluve/graphql-types';
import { useTranslation } from '@eluve/i18n';
import {
  LLM_OUTPUT_TYPE_VARIABLES,
  convertDynamicArtifactToJsonSchema,
} from '@eluve/llm-outputs';

import { BlockPicker } from './BlockPicker';
import { SortableTreeItem } from './SortableTreeItem';
import { templateBuilderStore } from './templateBuilderStore';
import { useIsStateDirty } from './useIsStateDirty';
import { areAllBlocksValid, flattenBlocks, getProjection } from './utilities';

type ValidationError = {
  message: string;
  field?: 'name' | 'description';
};

const adjustTranslate: Modifier = ({ transform }) => {
  return {
    ...transform,
    y: transform.y - 25,
  };
};

const dropAnimationConfig: DropAnimation = {
  keyframes({ transform }) {
    return [
      { opacity: 1, transform: CSS.Transform.toString(transform.initial) },
      {
        opacity: 0,
        transform: CSS.Transform.toString({
          ...transform.final,
          x: transform.final.x + 5,
          y: transform.final.y + 5,
        }),
      },
    ];
  },
  easing: 'ease-out',
  sideEffects({ active }) {
    active.node.animate([{ opacity: 0 }, { opacity: 1 }], {
      duration: defaultDropAnimation.duration,
      easing: defaultDropAnimation.easing,
    });
  },
};

const PREVENT_RELOAD_REASON = 'template-dirty';

export const DynamicArtifactTemplateBuilder: React.FC<{
  onIsValidChange?: (isValid: boolean) => void;
  isReadOnly?: boolean;
  showJsonSchema?: boolean;
  hideTemplateDetails?: boolean;
}> = ({
  onIsValidChange,
  isReadOnly = false,
  showJsonSchema = false,
  hideTemplateDetails = false,
}) => {
  const toast = useToast();
  const { t } = useTranslation();
  const indentationWidth = 24;
  const indicator = true;
  const isValid = useRef(false);
  const [validationErrors, setValidationErrors] = useState<ValidationError[]>(
    [],
  );
  const onIsValidChangeRef = useLatest(onIsValidChange);

  useEffect(() => {
    templateBuilderStore.send({
      type: 'setIsReadOnly',
      isReadOnly,
    });
  }, [isReadOnly]);

  const blocks = useSelector(
    templateBuilderStore,
    (state) => state.context.blocks,
  );

  const outputType = useSelector(
    templateBuilderStore,
    (state) => state.context.outputType,
  );

  const name = useSelector(
    templateBuilderStore,
    ({ context: { name } }) => name,
  );

  const description = useSelector(
    templateBuilderStore,
    ({ context: { description } }) => description,
  );

  const variantNotes = useSelector(
    templateBuilderStore,
    ({ context: { variantNotes } }) => variantNotes,
  );

  const isImported = useSelector(
    templateBuilderStore,
    ({ context: { isImported } }) => isImported,
  );

  const exportableJson = useSelector(
    templateBuilderStore,
    ({ context: { exportableJson } }) => exportableJson,
  );

  const isDirty = useIsStateDirty();

  const [activeId, setActiveId] = useState<UniqueIdentifier | null>(null);
  const [overId, setOverId] = useState<UniqueIdentifier | null>(null);
  const [offsetLeft, setOffsetLeft] = useState(0);

  const resetState = () => {
    setOverId(null);
    setActiveId(null);
    setOffsetLeft(0);

    document.body.style.setProperty('cursor', '');
  };

  const flattenedBlocks = useMemo(() => flattenBlocks(blocks), [blocks]);

  useEffect(() => {
    const subscription = templateBuilderStore.subscribe((state) => {
      const { context } = state;
      const validationErrors: ValidationError[] = [];

      const allBlocksValid = areAllBlocksValid(context.blocks);
      const flatBlocks = flattenBlocks(context.blocks);
      if (!allBlocksValid) {
        validationErrors.push({
          message: t('smartBlocks.templateBuilder.allBlocksMustBeValid'),
        });
      }

      const hasAtLeastOneNonGroupBlock =
        flatBlocks.length > 0 && flatBlocks.some((b) => b.type !== 'group');

      if (!hasAtLeastOneNonGroupBlock) {
        validationErrors.push({
          message: t('smartBlocks.templateBuilder.atLeastOneBlockRequired'),
        });
      }

      if (!hideTemplateDetails && !context.name) {
        validationErrors.push({
          message: t('smartBlocks.templateBuilder.requiredField'),
          field: 'name',
        });
      }

      if (!hideTemplateDetails && !context.description) {
        validationErrors.push({
          message: t('smartBlocks.templateBuilder.requiredField'),
          field: 'description',
        });
      }

      const isTemplateValid = validationErrors.length === 0;

      setValidationErrors(validationErrors);

      if (isValid.current !== isTemplateValid) {
        isValid.current = isTemplateValid;
        onIsValidChangeRef.current?.(isTemplateValid);
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [hideTemplateDetails, onIsValidChangeRef, t]);

  // Prevent navigation/reload when template has unsaved changes
  // This combines version checker prevention and browser navigation prevention
  useEffect(() => {
    const shouldPrevent = isDirty && !isReadOnly;

    if (shouldPrevent) {
      usePreventAutoReloadOnNewVersion
        .getState()
        .addReason(PREVENT_RELOAD_REASON);
    } else {
      usePreventAutoReloadOnNewVersion
        .getState()
        .removeReason(PREVENT_RELOAD_REASON);
    }

    if (!shouldPrevent) {
      return;
    }

    const beforeUnloadHandler = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };

    window.addEventListener('beforeunload', beforeUnloadHandler);

    return () => {
      window.removeEventListener('beforeunload', beforeUnloadHandler);
    };
  }, [isDirty, isReadOnly]);

  useUnmount(() => {
    usePreventAutoReloadOnNewVersion
      .getState()
      .removeReason(PREVENT_RELOAD_REASON);
  });

  const templateNameHasErrors =
    validationErrors.find((e) => e.field === 'name') && !isReadOnly;
  const templateDescriptionHasErrors =
    validationErrors.find((e) => e.field === 'description') && !isReadOnly;

  const projected =
    activeId && overId
      ? getProjection(
          flattenedBlocks,
          activeId,
          overId,
          offsetLeft,
          indentationWidth,
        )
      : null;

  const sortedIds = useMemo(
    () => flattenedBlocks.map((block) => block.id),
    [flattenedBlocks],
  );

  const activeBlock = activeId
    ? flattenedBlocks.find((b) => b.id === activeId)
    : null;

  const handleDragStart = (evt: DragStartEvent) => {
    const {
      active: { id },
    } = evt;
    setActiveId(id);
    setOverId(id);
    document.body.style.setProperty('cursor', 'grabbing');
  };

  const handleDragEnd = (evt: DragEndEvent) => {
    const { over, active } = evt;
    resetState();

    if (projected && over) {
      const { parentId } = projected;
      const movedBlock = flattenedBlocks.find((b) => b.id === active.id);
      if (!movedBlock) {
        return;
      }

      if (parentId === movedBlock.parentId) {
        templateBuilderStore.send({
          type: 'moveInsideGroup',
          parentId,
          activeId: active.id,
          overId: over.id,
        });
      } else {
        templateBuilderStore.send({
          type: 'moveBlockBetweenGroups',
          from: movedBlock.parentId,
          to: {
            id: parentId,
            activeId: active.id,
            overId: over.id,
          },
        });
      }
    }
  };

  const handleDragOver = (evt: DragOverEvent) => {
    setOverId(evt?.over?.id ?? null);
  };

  const handleDragMove = (evt: DragMoveEvent) => {
    setOffsetLeft(evt.delta.x);
  };

  const jsonSchema = convertDynamicArtifactToJsonSchema({
    name,
    description,
    blocks,
  }).valueOf();

  return (
    <Stack className="min-w-0" gap={4}>
      <Stack justify="between" gap={2} direction="horizontal">
        <Heading size="lg">
          {t('smartBlocks.templateBuilder.aboutThisTemplate')}
        </Heading>
        <Stack fullWidth={false} gap={2} direction="horizontal">
          {isReadOnly && exportableJson && (
            <Sheet>
              <SheetTrigger asChild>
                <Button variant="outline">
                  <Icon name="ClipboardCopy" size="auto" />
                  {t('smartBlocks.templateBuilder.export')}
                </Button>
              </SheetTrigger>
              <SheetContent side="bottom">
                <SheetHeader className="flex-row items-center justify-between gap-2 pr-16">
                  <SheetTitle>
                    {t('smartBlocks.templateBuilder.exportTemplate')}
                  </SheetTitle>
                  <Button
                    onClick={async () => {
                      await navigator.clipboard.writeText(exportableJson);
                      toast.success(
                        t('smartBlocks.templateBuilder.jsonSchemaCopied'),
                      );
                    }}
                  >
                    <Icon name="ClipboardCopy" size="auto" />
                    {t('smartBlocks.templateBuilder.copyJsonSchema')}
                  </Button>
                </SheetHeader>
                <div className="container flex h-[80vh] flex-col pb-4">
                  <Textarea
                    value={exportableJson}
                    className="w-full grow font-mono shadow-sm"
                    placeholder={t(
                      'smartBlocks.templateBuilder.pasteJsonSchemaPlaceholder',
                    )}
                    readOnly
                  />
                </div>
              </SheetContent>
            </Sheet>
          )}
        </Stack>
      </Stack>
      <Stack gap={4}>
        <FormItem className="w-full">
          <Label htmlFor="template-name">
            {t('smartBlocks.templateBuilder.nameLabel')}
          </Label>
          <Input
            value={name}
            disabled={
              isReadOnly || (isImported ?? false) || hideTemplateDetails
            }
            onChange={(e) => {
              templateBuilderStore.send({
                type: 'setName',
                name: e.target.value,
              });
            }}
            placeholder={t('smartBlocks.templateBuilder.nameLabel')}
            id="template-name"
          />
          {templateNameHasErrors && (
            <Text className="text-text-negative" color="inherit">
              {validationErrors.find((e) => e.field === 'name')?.message}
            </Text>
          )}
        </FormItem>

        <FormItem className="w-full">
          <Label htmlFor="output-type">
            {t('smartBlocks.templateBuilder.typeLabel')}
          </Label>
          <Select
            value={outputType}
            disabled={
              isReadOnly || (isImported ?? false) || hideTemplateDetails
            }
            onValueChange={(val) => {
              templateBuilderStore.send({
                type: 'setOutputType',
                outputType: val as LlmOutputTypesEnum,
              });
            }}
          >
            <SelectTrigger id="output-type">
              <SelectValue
                placeholder={t(
                  'smartBlocks.templateBuilder.outputTypePlaceholder',
                )}
              />
            </SelectTrigger>
            <SelectContent>
              {Object.keys(LLM_OUTPUT_TYPE_VARIABLES)
                .filter(
                  (k) =>
                    k !== LlmOutputTypesLookup.DYNAMIC_OUTPUT &&
                    k !== LlmOutputTypesLookup.TRANSLATION,
                )
                .sort()
                .map((type) => (
                  <SelectItem key={type} value={type}>
                    {type.split('_').map(capitalize).join(' ')}
                  </SelectItem>
                ))}
            </SelectContent>
          </Select>
        </FormItem>

        <FormItem className="w-full">
          <Label htmlFor="template-description">
            {t('smartBlocks.templateBuilder.descriptionLabel')}
          </Label>
          <Textarea
            id="template-description"
            value={description}
            disabled={isReadOnly || hideTemplateDetails}
            onChange={(e) => {
              templateBuilderStore.send({
                type: 'setDescription',
                description: e.target.value,
              });
            }}
            placeholder={t(
              'smartBlocks.templateBuilder.descriptionPlaceholder',
            )}
          />
          {templateDescriptionHasErrors && (
            <Text className="text-text-negative" color="inherit">
              {validationErrors.find((e) => e.field === 'description')?.message}
            </Text>
          )}
        </FormItem>
      </Stack>
      <Separator />
      <Stack gap={2}>
        <Heading size="lg">
          {t('smartBlocks.templateBuilder.variantNotes')}
        </Heading>
        <Text size="xs" color="secondary">
          {t('smartBlocks.templateBuilder.variantNotesDescription')}
        </Text>
        <FormItem className="w-full">
          <Textarea
            value={variantNotes ?? ''}
            disabled={isReadOnly}
            onChange={(e) => {
              templateBuilderStore.send({
                type: 'setVariantNotes',
                variantNotes: e.target.value,
              });
            }}
            placeholder={t(
              'smartBlocks.templateBuilder.variantNotesPlaceholder',
            )}
            className="min-h-[80px]"
          />
        </FormItem>
      </Stack>
      <Separator />
      <Stack justify="between" gap={2} direction="horizontal">
        <Heading size="lg">{t('smartBlocks.templateBuilder.blocks')}</Heading>
        <Stack fullWidth={false} gap={2} direction="horizontal">
          {Boolean(flattenedBlocks.length > 0) && (
            <>
              <Button
                variant="outline"
                onClick={() =>
                  templateBuilderStore.send({
                    type: 'toggleCollapseAll',
                    collapsed: true,
                  })
                }
              >
                <Icon name="ChevronsDownUp" size="auto" />
                {t('smartBlocks.templateBuilder.collapseAll')}
              </Button>
              <Button
                variant="outline"
                onClick={() =>
                  templateBuilderStore.send({
                    type: 'toggleCollapseAll',
                    collapsed: false,
                  })
                }
              >
                <Icon name="ChevronsUpDown" size="auto" />
                {t('smartBlocks.templateBuilder.expandAll')}
              </Button>
            </>
          )}
          {showJsonSchema && (
            <Sheet>
              <SheetTrigger asChild>
                <Button variant="outline">
                  <Icon name="Braces" size="auto" />
                  {t('smartBlocks.templateBuilder.viewJson')}
                </Button>
              </SheetTrigger>
              <SheetContent side="bottom">
                <SheetHeader className="flex-row items-center justify-between gap-2 pr-16">
                  <SheetTitle>
                    {t('smartBlocks.templateBuilder.templateJson')}
                  </SheetTitle>
                  <Button
                    onClick={async () => {
                      try {
                        await navigator.clipboard.writeText(
                          JSON.stringify(jsonSchema),
                        );
                        toast.success(
                          t('smartBlocks.templateBuilder.templateJsonCopied'),
                        );
                      } catch {
                        toast.error(
                          t(
                            'smartBlocks.templateBuilder.templateJsonCopyFailed',
                          ),
                        );
                      }
                    }}
                  >
                    <Icon name="Copy" size="auto" />
                    {t('common.copyToClipboard')}
                  </Button>
                </SheetHeader>
                <div className="container h-[75vh] overflow-auto pb-4">
                  <DebugJson value={jsonSchema} />
                </div>
              </SheetContent>
            </Sheet>
          )}
        </Stack>
      </Stack>
      <Stack gap={6}>
        <DndContext
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
          onDragOver={handleDragOver}
          onDragMove={handleDragMove}
          onDragCancel={resetState}
          collisionDetection={closestCenter}
        >
          <SortableContext
            items={sortedIds}
            strategy={verticalListSortingStrategy}
          >
            {flattenedBlocks.map((block) => {
              const { id, collapsed, depth } = block;
              return (
                <SortableTreeItem
                  key={id}
                  id={id}
                  value={block}
                  depth={id === activeId && projected ? projected.depth : depth}
                  indentationWidth={indentationWidth}
                  indicator={indicator}
                  collapsed={Boolean(collapsed)}
                />
              );
            })}
            {createPortal(
              <DragOverlay
                modifiers={indicator ? [adjustTranslate] : undefined}
                dropAnimation={dropAnimationConfig}
              >
                {activeId && activeBlock ? (
                  <SortableTreeItem
                    id={activeId}
                    depth={activeBlock.depth}
                    value={activeBlock}
                    clone
                    indentationWidth={indentationWidth}
                    collapsed={true}
                  />
                ) : null}
              </DragOverlay>,
              document.body,
            )}
          </SortableContext>
        </DndContext>
        {flattenedBlocks.length ? <Separator className="my-2" /> : null}
        {!isReadOnly && !isImported && (
          <Stack gap={3}>
            <Text>{t('smartBlocks.templateBuilder.addBlock')}</Text>
            <BlockPicker parentId={null} showTips />
          </Stack>
        )}
      </Stack>
    </Stack>
  );
};
